import { defineField, defineType } from 'sanity'
import getPageInfo, { seoGroup } from '../objects/pageInfo'
import { strongEmLinkWithReadMore } from '../../data/richText'
import BuyerGuideContextPreview from '../../components/BuyerGuideContextPreview'
import TireAvailabilityScoringButton from '../../components/TireAvailabilityScoringButton'
import AIGenerationButton from '../../components/AIGenerationButton'
import PerformanceScoreButton from '../../components/PerformanceScoreButton'

export default defineType({
  name: 'buyerGuide',
  title: 'Buyer Guides',
  type: 'document',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    {
      name: 'fitments',
      title: 'Fitments',
    },
    {
      name: 'aiTools',
      title: 'Utilities',
    },
    seoGroup,
  ],
  fields: [
    getPageInfo({ path: 'fitmentGuide' }),
    defineField({
      name: 'publishedAt',
      title: 'Published at',
      type: 'datetime',
      options: {
        dateFormat: 'DD.MM.YYYY',
        timeFormat: 'HH:mm',
      },
      validation: (Rule) => Rule.required(),
      initialValue: new Date().toISOString(),
      group: 'content',
    }),

    defineField({
      name: 'title',
      title: 'Title',
      type: 'string',
      validation: (Rule) => Rule.required(),
      group: 'content',
    }),
    defineField({
      name: 'mainImage',
      title: 'Main Image',
      type: 'image',
      options: {
        hotspot: true,
      },
      group: 'content',
    }),
    defineField({
      name: 'content',
      title: 'Content',
      type: 'array',
      of: strongEmLinkWithReadMore,
      group: 'content',
    }),
    defineField({
      name: 'table',
      title: 'Table',
      type: 'table',
      group: 'content',
    }),
    defineField({
      title: 'Related articles',
      name: 'blog',
      type: 'object',
      fields: [
        {
          title: 'Title',
          name: 'title',
          type: 'string',
        },
        {
          name: 'articles',
          title: 'Featured articles',
          description:
            'Manually select articles. They lead the "Additional resources" list, ahead of the vehicle-tagged ones — a featured article does not need "Show On Fitment Guide" active.',
          type: 'array',
          of: [
            {
              type: 'reference',
              to: [{ type: 'article' }],
              options: {
                // Featured articles are cherry-picked by _id when the guide is rendered
                // and bypass the show* visibility flags, so the picker must not filter by
                // showOnWheelLandingPage — that hid every article not flagged for wheel
                // landing pages (AR-1825). It mirrors the article sync filter instead
                // (frontend server/sync/groq/sync.groq.ts → `articles`), so only articles
                // that actually reach the frontend can be selected.
                filter: `defined(pageInfo.canonical.current) &&
                  defined(publishedAt) &&
                  count(categories) > 0 &&
                  (showOnBlog == true ||
                    showOnHomePage == true ||
                    showInResourceCenter == true ||
                    showOnGenerationPage == true ||
                    showOnWheelLandingPage == true ||
                    showOnFitmentGuide == true)`,
              },
            },
          ],
          validation: (Rule) => [
            Rule.unique().error('Each article should be unique'),
          ],
        },
      ],
      group: 'content',
    }),
    defineField({
      title: 'FAQ',
      name: 'faq',
      type: 'faq',
      group: 'content',
    }),
    defineField({
      name: 'fitmentTypes',
      title: 'Fitment Types',
      group: 'fitments',
      type: 'array',
      of: [
        {
          type: 'object',
          fields: [
            {
              name: 'fitment',
              title: 'Fitment',
              type: 'reference',
              to: [{ type: 'fitment' }],
              options: {
                filter: ({ document, parent }) => {
                  const selectedFitments =
                    parent?.parent?.fitmentTypes
                      ?.map((ft: any) => ft.fitment?._ref)
                      .filter(Boolean) || []

                  return {
                    filter: '!(_id in $ids)',
                    params: {
                      ids: selectedFitments,
                    },
                  }
                },
              },
            },
            {
              type: 'array',
              name: 'listingItems',
              title: 'Listing Items',
              of: [
                {
                  type: 'fitmentListing',
                },
              ],
            },
          ],
          preview: {
            select: {
              fitmentName: 'fitment.name',
              listingItems: 'listingItems',
            },
            prepare({ fitmentName, listingItems = [] }: any) {
              const itemsCount = listingItems?.length || 0
              return {
                title: fitmentName || 'No fitment selected',
                subtitle: `${itemsCount} listing item${itemsCount === 1 ? '' : 's'}`,
              }
            },
          },
        },
      ],
      validation: (Rule) =>
        Rule.unique().custom((fitmentTypes: any[] = []) => {
          const fitmentRefs = fitmentTypes
            .map((ft) => ft.fitment?._ref)
            .filter(Boolean)
          const uniqueRefs = [...new Set(fitmentRefs)]

          if (fitmentRefs.length !== uniqueRefs.length) {
            return 'Each fitment can only be used once'
          }
          return true
        }),
    }),
    defineField({
      name: 'contextPreview',
      title: 'Context Preview',
      type: 'object',
      group: 'aiTools',
      components: {
        input: BuyerGuideContextPreview,
      },
      fields: [
        {
          name: 'placeholder',
          type: 'string'
        }
      ]
    }),
    defineField({
      name: 'tireAvailabilityScoring',
      title: 'Tire Availability Scoring',
      type: 'object',
      group: 'aiTools',
      components: {
        input: TireAvailabilityScoringButton,
      },
      fields: [
        {
          name: 'placeholder',
          type: 'string'
        }
      ]
    }),
    defineField({
      name: 'performanceScoreCalculator',
      title: 'Performance Score Calculator',
      type: 'object',
      group: 'aiTools',
      components: {
        input: PerformanceScoreButton,
      },
      fields: [
        {
          name: 'placeholder',
          type: 'string'
        }
      ]
    }),
    defineField({
      name: 'aiGeneration',
      title: 'AI Content Generation',
      type: 'object',
      group: 'aiTools',
      components: {
        input: AIGenerationButton,
      },
      fields: [
        {
          name: 'placeholder',
          type: 'string'
        }
      ]
    }),
  ],
  preview: {
    select: {
      title: 'pageInfo.title',
      lastEdited: '_updatedAt',
      fitmentTypes: 'fitmentTypes',
      slug: 'pageInfo.canonical.current',
    },
    prepare({ title = 'New page', lastEdited, fitmentTypes, slug }: any) {
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }).format(new Date(lastEdited))

      const subtitle = `Last edited: ${formattedDate}`
      let errorSubtitle
      let warningSubtitle

      if (!fitmentTypes?.length) warningSubtitle = 'Warning (No fitments)'

      if (!slug) errorSubtitle = 'Invalid (Missing slug)'

      return {
        title,
        subtitle: errorSubtitle || warningSubtitle || subtitle,
      }
    },
  },
})
